import { defaultDataIdFromObject } from '@apollo/client';
import type { FieldFunctionOptions, TypePolicies } from '@apollo/client';
import mergeWithKey from 'ramda/es/mergeWithKey';
import uniqBy from 'ramda/es/uniqBy';
import { mergeProvenanceData } from './cache/cacheUtils';

const mergeById =
  (idKey: string) =>
  (existing: Readonly<any>, incoming: Readonly<any>, { readField, args }: any) => {
    if (!args?.cursor) {
      // No cursor means fresh fetch — use incoming directly to preserve server ordering.
      return { ...incoming };
    }

    // Pagination — append new page items, deduplicating by key.
    const seen = new Set<string>();
    const items: any[] = [];

    for (const item of existing?.items ?? []) {
      const key = readField(idKey, item) as string;
      if (key != null && !seen.has(key)) {
        seen.add(key);
        items.push(item);
      }
    }

    for (const item of incoming.items ?? []) {
      const key = readField(idKey, item) as string;
      if (key != null && !seen.has(key)) {
        seen.add(key);
        items.push(item);
      }
    }

    return { ...incoming, items };
  };

export const typePolicies: TypePolicies = {
  Query: {
    fields: {
      worklistItems: {
        // Every argument except `limit` and `cursor`, so all pages share one entry.
        keyArgs: [
          'sortOrder',
          'sortColumn',
          'statsOnTop',
          'claimedBy',
          'site',
          'patientType',
          'patientSex',
          'patientDob',
          'modality',
          'priority',
          'status',
          'filter',
          'smids',
          'mrn',
          'accessionNumber',
          'accessionNumberPartial',
          'reportExtendedStatus',
          'activeViews',
          'bodyPart',
          'claimedByPartial',
          'criticalResult',
          'customStatus',
          'examCode',
          'hasDraftReport',
          'isActive',
          'issuer',
          'mrnPartial',
          'patientName',
          'priorityFilter',
          'qcStatus',
          'referringPhysician',
          'referringPhysicianPartial',
          'scopeObjectId',
          'signedBy',
          'specialty',
          'studyDate',
          'studyDescription',
          'submittedAt',
        ],
        merge: mergeById('smid'),
      },
      searchPatients: {
        keyArgs: ['mrn', 'firstName', 'lastName', 'dateOfBirth', 'limit'],
        merge: mergeById('smid'),
      },
      hangingProtocols: {
        merge: false,
      },
      toolPreferences: {
        merge: false,
      },
      studyDescriptions: {
        keyArgs: ['search'],
      },
      studies: {
        keyArgs: [
          'smids',
          'filter',
          'modality',
          'mrn',
          'bodyPart',
          'dateRange',
          'sortColumn',
          'sortOrder',
          'searchSmid',
          'overrideShowRelevantPriorsOnly',
        ],
        merge: mergeById('smid'),
      },
      report: {
        keyArgs: ['smid'],
        merge: true,
      },
      templates: {
        keyArgs: ['owners', 'procedures', 'search', 'order'],
        merge: (existing, incoming, { readField }) => {
          const uniqueItems = (k: string, l: any[], r: any[]) => {
            return k === 'items'
              ? uniqBy((item) => readField<string>('id', item), [...l, ...r])
              : r;
          };

          return mergeWithKey(uniqueItems, existing, incoming);
        },
      },
      macros: {
        keyArgs: ['owners', 'procedures', 'search', 'order'],
        merge: (existing = { items: [] }, incoming, { readField }) => {
          return {
            ...incoming,
            items: uniqBy(
              (item) => readField('smid', item),
              [...existing.items, ...incoming.items]
            ),
          };
        },
      },
      addendums: {
        merge: (existing = [], incoming, args) => {
          return uniqBy((item) => item.__ref, [...existing, ...incoming]);
        },
      },
    },
  },
  ProvenanceData: {
    merge: mergeProvenanceData,
  },
  User: {
    fields: {
      permissions: {
        merge(existing: object, incoming: JSON, args: FieldFunctionOptions) {
          return Object.keys(incoming).length > 0 ? incoming : existing;
        },
      },
    },
  },
  ProcedureReference: {
    // Include 'smid' and 'autoload' as keyFields to uniquely identify ProcedureReferences.
    // This accounts for cases where the same 'smid' has different 'autoload' values in
    // different queries (GET_REPORT_TEMPLATE vs. GET_CURRENT_WORKLIST_REPORT), ensuring
    // accurate cache representation for each context.
    keyFields: ['smid', 'autoload'],
  },
  StudyDescriptions: {
    fields: {
      items: {
        merge(existing: any[] = [], incoming: any[], args: FieldFunctionOptions) {
          return uniqBy((item) => item.__ref, [...existing, ...incoming]);
        },
      },
    },
  },
  Macros: {
    fields: {
      items: {
        merge(existing: any[] = [], incoming: any[], args: FieldFunctionOptions) {
          return uniqBy((item) => item.__ref, [...existing, ...incoming]);
        },
      },
    },
  },
  Annotation: {
    fields: {
      data: {
        merge: false,
      },
    },
  },
  ReporterSettings: {
    keyFields: ['styles', 'mergeFieldsSettings'],
  },
  WorklistColumn: {
    keyFields: ['id', 'enabled', 'width', 'pinPosition', 'order'],
  },
  WorkspacePreset: {
    fields: {
      windows: {
        merge: false,
      },
    },
  },
  DicomTag: {
    keyFields: false,
  },
  // Layout window IDs are Chrome window IDs, shared by every layout captured from the same windows
  WindowLayout: {
    keyFields: false,
  },

  // Disable Frame caching as it can slow down the app a lot
  Frame: {
    keyFields: false,
  },

  // Same reason: Series and Stack run 5-50x per study in GET_BASE_VIEWER_DATA, so normalizing each one dominates `processSelectionSet` (~1s on prior-chip add). Study stays normalized for cross-query field merging (RP-4243 / RP-4392).
  Series: { keyFields: false },
  SingleLayerStack: { keyFields: false },
  LayeredStack: { keyFields: false },
  ImageRegistration: { keyFields: false },
  SeriesCriteria: { keyFields: false },
  SeriesClassification: { keyFields: false },
  SeriesClassificationSummary: { keyFields: false },
  DehydratedSeries: { keyFields: false },
  DehydratedStack: { keyFields: false },

  // Structured Report Data are only pulled at the top level
  SRStudy: {
    keyFields: false,
  },
  SRSeries: {
    keyFields: false,
  },
  SRInstance: {
    keyFields: false,
  },
  SRGeneralInfo: {
    keyFields: false,
  },
  SRObservation: {
    keyFields: false,
  },
  SRSOPClass: {
    keyFields: false,
  },
};

export const dataIdFromObject: typeof defaultDataIdFromObject = (responseObject) => {
  // HangingProtocolLayout has an `id` property that describes the window ID rather than a unique identifier.
  if (
    responseObject.__typename === 'HangingProtocolLayout' &&
    Array.isArray(responseObject.layout)
  ) {
    return `${responseObject.__typename}:${responseObject.id}:${responseObject.layout.join('-')}`;
  }

  // Apollo defaults to `id` or `_id`; use the repository's usual `smid` key when available.
  if (responseObject.id == null && responseObject._id == null && responseObject.smid != null) {
    return `${responseObject.__typename}:${responseObject.smid}`;
  }

  return defaultDataIdFromObject(responseObject);
};
